/**
 * RegisterPaymentPage (billing) — Registrar Pagamento (POST /billing/payments).
 *
 * 3 etapas:
 *  1) Dados do Recebimento — tudo do payload fora de invoices[] (valor, data+hora, meio/tipo,
 *     conta de recebimento, pagador com busca no person service, comprovante [placeholder]).
 *  2) Selecionar Contratos — busca no contracts-management + seleção de operações.
 *  3) Selecionar Faturas — invoices OPEN das operações; por fatura: value/total_payment/
 *     cancel_invoice/uncollect. Barra de alocação até 100% do valor informado (submete só em 100%).
 *
 * TODO(comprovante): upload de comprovante ainda sem backend (file_name_receipt/file_url_receipt).
 */
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    FaFileInvoiceDollar, FaChevronDown, FaChevronUp, FaCheckSquare, FaRegSquare,
    FaSearch, FaUserCheck, FaUserSlash, FaCloudUploadAlt, FaTimes, FaSpinner,
} from 'react-icons/fa';
import { Auth } from 'aws-amplify';
import ReceivingBankAccountSelect from '../settlement/components/ReceivingBankAccountSelect';
import { listInvoicesByOperation, processPayment, PaymentInvoiceItem } from './services/billingApi';
import { contractsManagementApi, OperationResult } from '../../../services/contractsManagementApi';
import { personApi, PersonAddress } from '../../../services/personApi';
import appConfig from '../../../config/app-config';
import { formatTaxId } from '../../../utils/taxIdUtils';

const STEPS = ['Dados do Recebimento', 'Selecionar Contratos', 'Selecionar Faturas'] as const;

const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const onlyDigits = (s: string) => (s || '').replace(/\D/g, '');
// "2.500.000,00" | "2500000.00" → number
const parseBRL = (s: string) => {
    const cleaned = (s || '').trim();
    if (!cleaned) return 0;
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
};
const round2 = (n: number) => Math.round(n * 100) / 100;

interface Invoice {
    id: number;
    invoiceId: string;
    chargeId: string | null;
    creditOperationId: string;
    amount: number;
    amountPaid: number | null;
    dueDate: string;
    status: string;
    statusFormatted?: string;
    documentTitle?: string | null;
    installmentNumber?: number | null;
    payer?: { name: string; documentNumber: string | null };
    _op?: string; // nº do contrato (para exibição)
}

interface ItemEdit {
    value: string;          // BRL string editável
    total_payment: boolean;
    cancel_invoice: boolean;
    uncollect: boolean;
}

const emptyPayer = {
    document: '', name: '', personId: '' as string | null,
    email: '', phone: '',
    address_street: '', address_number: '', address_complement: '',
    address_neighborhood: '', address_city: '', address_state: '', address_zip_code: '',
    lookup: 'idle' as 'idle' | 'searching' | 'found' | 'notfound',
};

const RegisterPaymentPage = () => {
    const [step, setStep] = useState(0);

    // ── Step 1 ──
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [time, setTime] = useState('00:00');
    const [showTime, setShowTime] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<'PIX' | 'BOLETO' | 'TRANSFER'>('PIX');
    const [paymentType, setPaymentType] = useState<'FINANCIAL' | 'DEBT_CONFESSION' | 'ASSET_DATION'>('FINANCIAL');
    const [account, setAccount] = useState('');
    const [userResponsible, setUserResponsible] = useState('');
    const [notes, setNotes] = useState('');
    const [receiptName, setReceiptName] = useState('');
    const [payer, setPayer] = useState(emptyPayer);
    const [payerExpanded, setPayerExpanded] = useState(false);

    // ── Step 2 ──
    const [search, setSearch] = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<OperationResult[]>([]);
    const [selectedOps, setSelectedOps] = useState<OperationResult[]>([]);
    const [manualIds, setManualIds] = useState(''); // provisório: credit_operation_id na mão

    // ── Step 3 ──
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [edits, setEdits] = useState<Record<string, ItemEdit>>({}); // key = invoiceId (selecionados)
    const [invSearch, setInvSearch] = useState('');
    const [invSort, setInvSort] = useState<'dueDate' | 'amount' | 'payer'>('dueDate');
    const [submitting, setSubmitting] = useState(false);
    // resumo da operação (contrato/aditamento/valor/pago) p/ cabeçalho dos grupos — via settlement /operations
    const [opSummaries, setOpSummaries] = useState<Record<string, { label: string; amount: number; paid: number }>>({});
    const [openSel, setOpenSel] = useState(true);   // accordion: faturas selecionadas
    const [openList, setOpenList] = useState(true);  // accordion: listagem/seleção

    const paymentAmount = round2(parseBRL(amount));

    // ─── Step 1: pagador — busca no person service ───────────────────────────
    const lookupPayer = useCallback(async () => {
        const doc = onlyDigits(payer.document);
        if (doc.length !== 11 && doc.length !== 14) {
            toast.warn('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos)');
            return;
        }
        setPayer(p => ({ ...p, lookup: 'searching' }));
        try {
            const found = await personApi.searchByDocument(doc);  // consolidado CPF/CNPJ
            if (!found) {
                setPayer(p => ({ ...p, personId: null, lookup: 'notfound' }));
                return;
            }
            const email = found.contacts?.find(c => /mail/i.test(c.type))?.content || '';
            const phone = found.contacts?.find(c => /(phone|mobile|cel|tel|whats)/i.test(c.type))?.content || '';
            // endereço (opcional) — preenche o que a base tiver (cidade/UF vêm por cityId, não resolvidos aqui)
            let addr: PersonAddress | undefined;
            try { addr = (await personApi.getAddresses(found.uuid))[0]; } catch { /* sem endereço */ }
            setPayer(p => ({
                ...p, name: (found.name || '').trim() || p.name, personId: found.uuid,
                email: email || p.email, phone: phone || p.phone,
                address_zip_code: addr?.zipcode || p.address_zip_code,
                address_street: addr ? [addr.streetType, addr.street].filter(Boolean).join(' ') : p.address_street,
                address_number: addr?.number || p.address_number,
                address_complement: addr?.complement || p.address_complement,
                address_neighborhood: addr?.district || p.address_neighborhood,
                lookup: 'found',
            }));
        } catch {
            setPayer(p => ({ ...p, personId: null, lookup: 'notfound' }));
        }
    }, [payer.document]);

    // pagador vindo do cadastro (person) não é editável aqui (alterar é no cadastro).
    const payerLocked = payer.lookup === 'found';
    const payerBusy = payer.lookup === 'searching';          // buscando no person → trava tudo
    const payerDisabled = payerLocked || payerBusy;          // campos da família pagador

    const step1Valid = paymentAmount > 0 && !!date && !!account && onlyDigits(payer.document).length >= 11;

    // ─── Step 2: busca de operações (contracts-management) ───────────────────
    const runSearch = async () => {
        if (!search.trim()) return;
        setSearching(true);
        try {
            setResults(await contractsManagementApi.searchOperations(search.trim()));
        } catch (e: any) {
            toast.error(`Erro ao buscar operações: ${e.message}`);
        } finally {
            setSearching(false);
        }
    };

    // Provisório: adicionar operações por credit_operation_id colado à mão (enquanto a busca
    // do contracts-management não está disponível). Aceita vários (vírgula/espaço/linha).
    const addManualOps = () => {
        const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const tokens = manualIds.split(/[\s,;]+/).map(t => t.trim()).filter(Boolean);
        const valid = tokens.filter(t => uuidRe.test(t));
        const invalid = tokens.filter(t => !uuidRe.test(t));
        if (invalid.length) toast.warn(`Ignorados (não parecem UUID): ${invalid.join(', ')}`);
        if (!valid.length) return;
        setSelectedOps(prev => {
            const existing = new Set(prev.map(o => o.credit_operation_id));
            const additions = valid.filter(id => !existing.has(id)).map((id): OperationResult => ({
                credit_operation_id: id, sequencial_number: 0,
                operation_number: `manual ${id.slice(0, 8)}`, operation_type: 'MANUAL',
                status: '', approved_value: 0, nominal_value: null, loan_amount: null, participants: [],
            }));
            return [...prev, ...additions];
        });
        setManualIds('');
    };

    const toggleOp = (op: OperationResult) => {
        setSelectedOps(prev => prev.find(o => o.credit_operation_id === op.credit_operation_id)
            ? prev.filter(o => o.credit_operation_id !== op.credit_operation_id)
            : [...prev, op]);
    };
    const isOpSelected = (op: OperationResult) => !!selectedOps.find(o => o.credit_operation_id === op.credit_operation_id);

    // resumo das operações (contrato-aditamento | valor | pago) — reusa settlement /operations.
    // TODO: idealmente um endpoint/view próprio do billing para esta tela.
    const fetchOpSummaries = async (ops: OperationResult[]) => {
        try {
            const { address, key } = appConfig.apis.settlementV2;
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const map: Record<string, { label: string; amount: number; paid: number }> = {};
            await Promise.all(ops.map(async op => {
                try {
                    // o endpoint filtra por filter[credit_operation_id] (padrão FilterBuilder), não credit_operation_id puro
                    const qs = new URLSearchParams({ 'filter[credit_operation_id]': op.credit_operation_id, size: '1' });
                    const res = await fetch(`${address}/operations?${qs.toString()}`,
                        { headers: { 'x-api-key': key, Authorization: `Bearer ${token}` } });
                    if (!res.ok) return;
                    const o = ((await res.json()).list || [])[0];
                    if (o) map[op.credit_operation_id] = {
                        label: `${o.contract_number ?? '—'}${o.amendment_number != null ? '-' + o.amendment_number : ''}`,
                        amount: Number(o.amount || 0), paid: Number(o.paid_amount || 0),
                    };
                } catch { /* operação sem resumo no settlement */ }
            }));
            setOpSummaries(map);
        } catch { /* sem sessão/settlement — cabeçalho usa fallback */ }
    };

    // ─── Step 3: carregar invoices OPEN das operações selecionadas ───────────
    const goToInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const all: Invoice[] = [];
            for (const op of selectedOps) {
                const data = await listInvoicesByOperation(op.credit_operation_id);
                (data.list || [])
                    .filter((inv: Invoice) => inv.status === 'OPEN')
                    .forEach((inv: Invoice) => all.push({ ...inv, _op: op.operation_number }));
            }
            setInvoices(all);
            await fetchOpSummaries(selectedOps);
            setStep(2);
        } catch (e: any) {
            toast.error(`Erro ao carregar faturas: ${e.message}`);
        } finally {
            setLoadingInvoices(false);
        }
    };

    const defaultEdit = (inv: Invoice): ItemEdit => ({
        value: fmt(inv.amount), total_payment: true, cancel_invoice: true, uncollect: true,
    });

    const toggleInvoice = (inv: Invoice) => {
        setEdits(prev => {
            const next = { ...prev };
            if (next[inv.invoiceId]) delete next[inv.invoiceId];
            else next[inv.invoiceId] = defaultEdit(inv);
            return next;
        });
    };

    const patchEdit = (inv: Invoice, patch: Partial<ItemEdit>) => {
        setEdits(prev => {
            const cur = prev[inv.invoiceId];
            if (!cur) return prev;
            let next = { ...cur, ...patch };
            // value alterado → ajusta total_payment automaticamente (usuário ainda pode remarcar)
            if (patch.value !== undefined) {
                const v = round2(parseBRL(patch.value));
                if (v < inv.amount) next.total_payment = false;
                else if (v >= inv.amount) next.total_payment = true;
            }
            // cancel_invoice=true força uncollect=true (e trava); false libera uncollect
            if (patch.cancel_invoice === true) next.uncollect = true;
            return { ...prev, [inv.invoiceId]: next };
        });
    };

    const isSelected = (inv: Invoice) => !!edits[inv.invoiceId];

    const filteredInvoices = useMemo(() => {
        const q = invSearch.trim().toLowerCase();
        let list = invoices;
        if (q) {
            list = list.filter(inv =>
                (inv.payer?.name || '').toLowerCase().includes(q) ||
                (inv.chargeId || '').toLowerCase().includes(q) ||
                (inv.documentTitle || '').toLowerCase().includes(q) ||
                (inv._op || '').toLowerCase().includes(q));
        }
        const sorted = [...list].sort((a, b) => {
            if (invSort === 'amount') return b.amount - a.amount;
            if (invSort === 'payer') return (a.payer?.name || '').localeCompare(b.payer?.name || '');
            return (a.dueDate || '').localeCompare(b.dueDate || '');
        });
        return sorted;
    }, [invoices, invSearch, invSort]);

    // agrupa os invoices filtrados por operação, preservando a ordem das operações selecionadas
    const groups = useMemo(() => {
        const byOp: Record<string, Invoice[]> = {};
        for (const inv of filteredInvoices) (byOp[inv.creditOperationId] ||= []).push(inv);
        return selectedOps
            .map(op => ({ op, list: byOp[op.credit_operation_id] || [] }))
            .filter(g => g.list.length > 0);
    }, [filteredInvoices, selectedOps]);

    // invoices selecionados (na ordem em que foram carregados)
    const selectedInvoices = useMemo(() => invoices.filter(inv => edits[inv.invoiceId]), [invoices, edits]);

    const allocated = useMemo(() =>
        round2(Object.values(edits).reduce((s, e) => s + parseBRL(e.value), 0)), [edits]);
    const pct = paymentAmount > 0 ? Math.min(100, (allocated / paymentAmount) * 100) : 0;
    const over = allocated > paymentAmount;
    const canSubmit = paymentAmount > 0 && round2(allocated) === paymentAmount && Object.keys(edits).length > 0;

    // ─── Submit ──────────────────────────────────────────────────────────────
    const buildPayload = () => {
        const wall = `${date}T${showTime ? time : '00:00'}`;
        const payment_date = new Date(wall).toISOString(); // wall-clock local → instante UTC (sem D±1)
        const doc = onlyDigits(payer.document);
        const items: PaymentInvoiceItem[] = invoices
            .filter(inv => edits[inv.invoiceId])
            .map(inv => {
                const e = edits[inv.invoiceId];
                return {
                    credit_operation_id: inv.creditOperationId,
                    charge_id: inv.chargeId,
                    invoice_id: inv.invoiceId,
                    value: round2(parseBRL(e.value)),
                    total_payment: e.total_payment,
                    cancel_invoice: e.cancel_invoice,
                    uncollect: e.uncollect,
                };
            });
        return {
            payment_id: (crypto as any).randomUUID ? crypto.randomUUID() : undefined,
            amount: paymentAmount,
            payment_date,
            payment_method: paymentMethod,
            payment_type: paymentType,
            receiving_bank_account_id: account,
            user_responsible: userResponsible || undefined,
            file_name_receipt: receiptName || undefined,
            data: notes ? { notes } : undefined,
            payer: {
                external_id: payer.personId || undefined,  // ausente → billing cria person sem external_id
                name: payer.name || doc,                   // name é NOT NULL no billing; fallback documento
                document_number: doc,
                email: payer.email || undefined,
                phone: payer.phone || undefined,
                address_street: payer.address_street || undefined,
                address_number: payer.address_number || undefined,
                address_complement: payer.address_complement || undefined,
                address_neighborhood: payer.address_neighborhood || undefined,
                address_city: payer.address_city || undefined,
                address_state: payer.address_state || undefined,
                address_zip_code: payer.address_zip_code || undefined,
            },
            invoices: items,
        };
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await processPayment(buildPayload());
            const n = res?.data?.settled?.length ?? 0;
            toast.success(`Pagamento registrado — ${n} fatura(s) processada(s)`);
            // reset
            setStep(0); setAmount(''); setEdits({}); setInvoices([]);
            setSelectedOps([]); setResults([]); setSearch(''); setPayer(emptyPayer);
        } catch (e: any) {
            toast.error(`Erro ao registrar pagamento: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const input = 'w-full border rounded px-3 py-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-slate-800 disabled:cursor-not-allowed';
    const label = 'text-xs text-gray-500 dark:text-gray-400 block mb-1';

    const fmtDate = (d?: string) => (d ? d.split('-').reverse().join('/') : '—');

    // cabeçalho enriquecido do invoice (documento · pagador … valor / Fatura · op · Recebível · vence)
    const invHeader = (inv: Invoice) => (
        <div className="flex-1 min-w-0">
            <div className="flex justify-between gap-2">
                <span className="text-sm font-medium truncate">
                    {inv.payer?.documentNumber ? `${formatTaxId(inv.payer.documentNumber)} · ` : ''}{inv.payer?.name || '—'}
                </span>
                <span className="text-sm font-medium whitespace-nowrap">R$ {fmt(inv.amount)}</span>
            </div>
            <div className="flex justify-between gap-2 text-xs text-gray-500">
                <span className="truncate">
                    Fatura {inv.invoiceId.slice(0, 8)} · op. {inv.creditOperationId.slice(0, 8)}
                    {inv.chargeId ? ` · Recebível ${inv.chargeId.slice(0, 8)}` : ''}
                </span>
                <span className="whitespace-nowrap">vence {fmtDate(inv.dueDate)}</span>
            </div>
        </div>
    );

    const editControls = (inv: Invoice, e: ItemEdit) => (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2" onClick={ev => ev.stopPropagation()}>
            <div>
                <label className={label}>Valor pago (R$)</label>
                <input className={input} value={e.value} onChange={ev => patchEdit(inv, { value: ev.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm mt-5">
                <input type="checkbox" checked={e.total_payment} onChange={ev => patchEdit(inv, { total_payment: ev.target.checked })} />
                Quitação total do título
            </label>
            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={e.cancel_invoice} onChange={ev => patchEdit(inv, { cancel_invoice: ev.target.checked })} />
                Cancelar fatura original
            </label>
            <label className={`flex items-center gap-2 text-sm ${e.cancel_invoice ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={e.uncollect} disabled={e.cancel_invoice} onChange={ev => patchEdit(inv, { uncollect: ev.target.checked })} />
                Parar de cobrar fatura original
            </label>
        </div>
    );

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                    <FaFileInvoiceDollar className="text-green-600" /> Registrar Pagamento
                </h1>
                <p className="text-sm text-gray-500 mt-1">Um pagamento liquida uma ou mais faturas (cobrança)</p>
            </div>

            {/* Stepper */}
            <div className="flex items-center mb-8 bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm">
                {STEPS.map((s, idx) => (
                    <div key={s} className="flex items-center flex-1">
                        <div className={`flex items-center gap-2 ${idx <= step ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${idx < step ? 'bg-green-500 text-white' : idx === step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                {idx < step ? '✓' : idx + 1}
                            </span>
                            <span className="text-sm">{s}</span>
                        </div>
                        {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-4 ${idx < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
                    </div>
                ))}
            </div>

            {/* ═══ STEP 1 ═══ */}
            {step === 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
                    <h2 className="font-semibold text-gray-800 dark:text-white mb-4">Dados do Recebimento</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={label}>Valor Recebido (R$) *</label>
                            <input type="text" value={amount} onChange={e => setAmount(e.target.value)}
                                placeholder="2.500.000,00" className={`${input} text-lg font-semibold`} />
                        </div>
                        <div>
                            <label className={label}>Data do Pagamento *</label>
                            <div className="flex gap-2 items-center">
                                <input type="date" value={date} onChange={e => setDate(e.target.value)} className={input} />
                                {showTime ? (
                                    <input type="time" value={time} onChange={e => setTime(e.target.value)} className="border rounded px-2 py-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                                ) : (
                                    <button type="button" onClick={() => setShowTime(true)}
                                        className="text-xs text-blue-600 whitespace-nowrap hover:underline">+ horário</button>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className={label}>Tipo de Pagamento *</label>
                            <select value={paymentType} onChange={e => setPaymentType(e.target.value as any)} className={input}>
                                <option value="FINANCIAL">Financeiro</option>
                                <option value="DEBT_CONFESSION">Confissão de dívida</option>
                                <option value="ASSET_DATION">Dação em pagamento</option>
                            </select>
                        </div>
                        <div>
                            <label className={label}>Meio de Pagamento *</label>
                            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value as any)} className={input}>
                                <option value="PIX">PIX</option>
                                <option value="TRANSFER">Transferência (TED)</option>
                                <option value="BOLETO">Boleto</option>
                            </select>
                        </div>
                        <div>
                            <label className={label}>Conta de Recebimento *</label>
                            <ReceivingBankAccountSelect value={account} onChange={setAccount} className={input} />
                        </div>
                        <div>
                            <label className={label}>Responsável</label>
                            <input type="text" value={userResponsible} onChange={e => setUserResponsible(e.target.value)}
                                placeholder="Quem registrou" className={input} />
                        </div>
                    </div>

                    {/* Pagador */}
                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Pagador</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={label}>CPF/CNPJ de quem pagou *</label>
                                <div className="flex gap-2">
                                    <input type="text" value={payer.document} disabled={payerBusy}
                                        onChange={e => setPayer(p => ({ ...p, document: e.target.value, lookup: 'idle', personId: null }))}
                                        onBlur={lookupPayer} placeholder="00.000.000/0001-00" className={input} />
                                    <button type="button" onClick={lookupPayer} disabled={payerBusy} title="Buscar no cadastro (person)"
                                        className="px-3 rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed">
                                        {payerBusy ? <FaSpinner className="animate-spin" /> : <FaSearch />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className={label}>Nome de quem pagou</label>
                                <input type="text" value={payer.name} disabled={payerDisabled} onChange={e => setPayer(p => ({ ...p, name: e.target.value }))}
                                    placeholder="Preenchido pela busca (opcional)" className={input} />
                            </div>
                        </div>
                        {payer.lookup === 'found' && (
                            <div className="mt-2 text-xs text-green-600 flex items-center gap-1"><FaUserCheck /> Encontrado no cadastro (person). Dados vinculados.</div>
                        )}
                        {payer.lookup === 'notfound' && (
                            <div className="mt-2 text-xs text-yellow-600 flex items-center gap-1"><FaUserSlash /> Não encontrado no cadastro — o pagador será registrado sem vínculo (external_id). Você pode prosseguir.</div>
                        )}

                        <button type="button" onClick={() => setPayerExpanded(v => !v)}
                            className="mt-3 text-xs text-blue-600 flex items-center gap-1 hover:underline">
                            {payerExpanded ? <FaChevronUp /> : <FaChevronDown />} {payerExpanded ? 'Ocultar' : 'Mais dados do pagador (opcional)'}
                        </button>
                        {payerExpanded && (
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                <div><label className={label}>E-mail</label><input className={input} disabled={payerDisabled} value={payer.email} onChange={e => setPayer(p => ({ ...p, email: e.target.value }))} /></div>
                                <div><label className={label}>Telefone</label><input className={input} disabled={payerDisabled} value={payer.phone} onChange={e => setPayer(p => ({ ...p, phone: e.target.value }))} /></div>
                                <div><label className={label}>CEP</label><input className={input} disabled={payerDisabled} value={payer.address_zip_code} onChange={e => setPayer(p => ({ ...p, address_zip_code: e.target.value }))} /></div>
                                <div className="col-span-2"><label className={label}>Logradouro</label><input className={input} disabled={payerDisabled} value={payer.address_street} onChange={e => setPayer(p => ({ ...p, address_street: e.target.value }))} /></div>
                                <div><label className={label}>Número</label><input className={input} disabled={payerDisabled} value={payer.address_number} onChange={e => setPayer(p => ({ ...p, address_number: e.target.value }))} /></div>
                                <div><label className={label}>Complemento</label><input className={input} disabled={payerDisabled} value={payer.address_complement} onChange={e => setPayer(p => ({ ...p, address_complement: e.target.value }))} /></div>
                                <div><label className={label}>Bairro</label><input className={input} disabled={payerDisabled} value={payer.address_neighborhood} onChange={e => setPayer(p => ({ ...p, address_neighborhood: e.target.value }))} /></div>
                                <div><label className={label}>Cidade</label><input className={input} disabled={payerDisabled} value={payer.address_city} onChange={e => setPayer(p => ({ ...p, address_city: e.target.value }))} /></div>
                                <div><label className={label}>UF</label><input className={input} disabled={payerDisabled} maxLength={2} value={payer.address_state} onChange={e => setPayer(p => ({ ...p, address_state: e.target.value }))} /></div>
                            </div>
                        )}
                    </div>

                    {/* Comprovante (placeholder — backend pendente) */}
                    <div className="mt-6 border-t dark:border-slate-700 pt-4">
                        <label className={label}>Comprovante de pagamento
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">TODO: backend de upload pendente</span>
                        </label>
                        <label className="flex items-center gap-2 border border-dashed dark:border-slate-600 rounded px-3 py-3 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700">
                            <FaCloudUploadAlt /> {receiptName || 'Selecionar arquivo (não enviado ainda)'}
                            <input type="file" className="hidden" onChange={e => setReceiptName(e.target.files?.[0]?.name || '')} />
                        </label>
                    </div>

                    <div className="mt-4">
                        <label className={label}>Observações</label>
                        <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                            placeholder="Referência bancária, NF, etc." className={input} />
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button disabled={!step1Valid} onClick={() => setStep(1)}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">Avançar →</button>
                    </div>
                </div>
            )}

            {/* ═══ STEP 2 ═══ */}
            {step === 1 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                    <div className="p-4 border-b dark:border-slate-700">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-semibold text-gray-800 dark:text-white">Buscar Operações de Crédito</h2>
                            <div className="text-sm font-semibold text-green-700">Valor: R$ {fmt(paymentAmount)}</div>
                        </div>
                        <div className="flex gap-2">
                            <input value={search} onChange={e => setSearch(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && runSearch()}
                                placeholder="Nº do contrato, nome, documento..." className={input} />
                            <button onClick={runSearch} disabled={searching}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2">
                                <FaSearch /> {searching ? 'Buscando...' : 'Buscar'}
                            </button>
                        </div>

                        {/* Provisório: informar credit_operation_id na mão (busca do contracts-management indisponível) */}
                        <div className="mt-3 border border-dashed border-amber-300 dark:border-amber-700/60 rounded p-3 bg-amber-50/50 dark:bg-amber-900/10">
                            <label className="text-xs text-amber-700 dark:text-amber-400 block mb-1">
                                Provisório — credit_operation_id manual
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">TODO: substituir pela busca do contracts-management</span>
                            </label>
                            <div className="flex gap-2">
                                <textarea value={manualIds} onChange={e => setManualIds(e.target.value)}
                                    rows={2} placeholder="Cole um ou vários credit_operation_id (UUID), separados por vírgula/espaço/linha"
                                    className={`${input} font-mono text-xs`} />
                                <button onClick={addManualOps}
                                    className="px-4 py-2 bg-amber-600 text-white rounded-lg whitespace-nowrap hover:bg-amber-700">+ Adicionar</button>
                            </div>
                        </div>
                    </div>

                    {selectedOps.length > 0 && (
                        <div className="px-4 py-3 bg-blue-50 dark:bg-slate-700/40 border-b dark:border-slate-700">
                            <span className="text-xs font-semibold text-blue-700 uppercase">Selecionados:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {selectedOps.map(o => (
                                    <span key={o.credit_operation_id} className="inline-flex items-center gap-1 px-2 py-1 bg-white dark:bg-slate-800 border border-blue-200 dark:border-slate-600 rounded text-sm">
                                        {o.operation_number}
                                        <button onClick={() => toggleOp(o)} className="text-gray-400 hover:text-red-500 ml-1"><FaTimes size={11} /></button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {results.length > 0 ? (
                        <div className="divide-y dark:divide-slate-700 max-h-[50vh] overflow-y-auto">
                            {results.map(op => (
                                <div key={op.credit_operation_id} className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/40 cursor-pointer"
                                    onClick={() => toggleOp(op)}>
                                    <span className="mr-3">{isOpSelected(op) ? <FaCheckSquare className="text-blue-600 text-lg" /> : <FaRegSquare className="text-lg text-gray-400" />}</span>
                                    <div className="flex-1">
                                        <span className="font-medium">{op.operation_number}</span>
                                        <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 dark:bg-slate-700 rounded">{op.operation_type}</span>
                                    </div>
                                    <span className="text-sm text-gray-500">R$ {fmt(Number(op.approved_value || 0))}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-400">Busque operações para selecionar</div>
                    )}

                    <div className="p-4 border-t dark:border-slate-700 flex items-center justify-between bg-gray-50 dark:bg-slate-800">
                        <span className="text-sm text-gray-600 dark:text-gray-300">{selectedOps.length} operação(ões) selecionada(s)</span>
                        <div className="flex gap-3">
                            <button onClick={() => setStep(0)} className="px-4 py-2 border rounded-lg dark:border-slate-600">← Voltar</button>
                            <button disabled={selectedOps.length === 0 || loadingInvoices} onClick={goToInvoices}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                                {loadingInvoices ? 'Carregando...' : 'Avançar →'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══ STEP 3 ═══ */}
            {step === 2 && (
                <div className="space-y-4">
                    {/* Barra de alocação (sempre visível) */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4">
                        <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-600 dark:text-gray-300">Alocado: <b className={over ? 'text-red-600' : 'text-green-700'}>R$ {fmt(allocated)}</b> / R$ {fmt(paymentAmount)}</span>
                            <span className={`font-semibold ${over ? 'text-red-600' : round2(allocated) === paymentAmount ? 'text-green-600' : 'text-gray-500'}`}>
                                {over ? `Excede R$ ${fmt(allocated - paymentAmount)}` : round2(allocated) === paymentAmount ? '✓ 100%' : `Falta R$ ${fmt(paymentAmount - allocated)}`}
                            </span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 dark:bg-slate-700 rounded overflow-hidden">
                            <div className={`h-full transition-all ${over ? 'bg-red-500' : pct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>

                    {/* ── Accordion A: Faturas selecionadas (editar + enviar) ── */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                        <button onClick={() => setOpenSel(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                            <span>Faturas selecionadas: {selectedInvoices.length}</span>
                            {openSel ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                        </button>
                        {openSel && (
                            <div className="border-t dark:border-slate-700 max-h-[45vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-2">
                                {selectedInvoices.length === 0 && (
                                    <div className="p-4 text-center text-gray-400 text-sm">Nenhuma fatura selecionada — selecione na área abaixo</div>
                                )}
                                {selectedInvoices.map(inv => {
                                    const e = edits[inv.invoiceId];
                                    return (
                                        <div key={inv.invoiceId} className="bg-[#f0f8ff] dark:bg-slate-700/40 rounded-md border border-blue-200 dark:border-slate-700 px-4 py-3">
                                            <div className="flex items-start gap-3">
                                                {invHeader(inv)}
                                                <button onClick={() => toggleInvoice(inv)} title="Remover da seleção"
                                                    className="text-gray-400 hover:text-red-500 mt-0.5"><FaTimes /></button>
                                            </div>
                                            {e && editControls(inv, e)}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* ── Accordion B: Operações / Faturas (listagem + seleção) ── */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm overflow-hidden">
                        <button onClick={() => setOpenList(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-800 dark:text-white bg-gray-300 dark:bg-slate-700 hover:bg-gray-300/80">
                            <span>Operações: {selectedOps.length} · Faturas: {invoices.length}</span>
                            {openList ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
                        </button>
                        {openList && (
                            <div className="border-t dark:border-slate-700">
                                <div className="p-3 border-b dark:border-slate-700 flex flex-col sm:flex-row gap-2 sm:items-center">
                                    <input value={invSearch} onChange={e => setInvSearch(e.target.value)}
                                        placeholder="Filtrar por pagador, recebível, contrato..." className={`${input} flex-1`} />
                                    <div className="flex items-center gap-2 whitespace-nowrap">
                                        <span className="text-xs text-gray-500">Ordenar:</span>
                                        <select value={invSort} onChange={e => setInvSort(e.target.value as any)} className={`${input} w-auto`}>
                                            <option value="dueDate">Vencimento</option>
                                            <option value="amount">Valor</option>
                                            <option value="payer">Pagador</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="max-h-[45vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/40 p-3 space-y-3">
                                    {groups.length === 0 && <div className="p-8 text-center text-gray-400">Nenhuma fatura OPEN encontrada</div>}
                                    {groups.map(({ op, list }) => {
                                        const s = opSummaries[op.credit_operation_id];
                                        return (
                                            <div key={op.credit_operation_id} className="rounded-md border border-gray-300 dark:border-slate-700 overflow-hidden">
                                                <div className="px-4 py-2 bg-gray-200 dark:bg-slate-700/50 text-sm font-medium flex flex-wrap gap-x-3 gap-y-1">
                                                    <span>{s?.label || op.operation_number}</span>
                                                    {s && <span className="text-gray-500 dark:text-gray-400 font-normal">| Valor: R$ {fmt(s.amount)}</span>}
                                                    {s && <span className="text-gray-500 dark:text-gray-400 font-normal">| Pago: R$ {fmt(s.paid)}</span>}
                                                    <span className="text-gray-500 dark:text-gray-400 font-normal">| Faturas: {list.length}</span>
                                                </div>
                                                <div className="p-2 space-y-1.5 bg-gray-100/70 dark:bg-slate-800/40">
                                                    {list.map(inv => {
                                                        const sel = isSelected(inv);
                                                        return (
                                                            <div key={inv.invoiceId}
                                                                className={`flex items-center gap-3 px-3 py-2 rounded border cursor-pointer ${sel ? 'border-blue-300 dark:border-blue-500 bg-[#f0f8ff] dark:bg-slate-700/50' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/30'}`}
                                                                onClick={() => toggleInvoice(inv)}>
                                                                <span>{sel ? <FaCheckSquare className="text-blue-600" /> : <FaRegSquare className="text-gray-300" />}</span>
                                                                {invHeader(inv)}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-4 flex items-center justify-between">
                        <button onClick={() => setStep(1)} className="px-4 py-2 border rounded-lg dark:border-slate-600">← Voltar</button>
                        <button disabled={!canSubmit || submitting} onClick={submit}
                            title={canSubmit ? '' : 'A soma dos valores deve ser exatamente 100% do valor recebido'}
                            className="px-6 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 hover:bg-green-700">
                            {submitting ? 'Registrando...' : 'Confirmar Pagamento'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterPaymentPage;
